<template>
  <nav class="header-menu">
    <div class="seo"><slot /></div>

    <!-- mobile -->
    <transition v-if="!$root.isDesktop" name="menu">
      <transition-group v-if="state" name="menulevel" tag="div" class="menu-levels mobile" :style="{ top: `${$root.header.inViewportHeight}px` }">
        <header-menu-level v-for="level in activeLevels" :key="level.id" :level="level" @menuitem:back="navigateUp" @menuitem:goto="navigateDown">
          <template v-if="level.depth === 1" #metanavigation>
            <slot name="metanavigation" />
          </template>
        </header-menu-level>
      </transition-group>
    </transition>

    <!-- desktop -->
    <transition-expand v-else name="menu">
      <div v-show="$root.page.scrollTop === 0 || $root.page.scrollingUp" class="menu-levels desktop">
        <header-menu-level :level="levels" />
      </div>
    </transition-expand>
  </nav>
</template>

<script>
import { COMPONENT_HEADERMENU_MAX_DEPTH_LEVEL } from '@/constants'

import { guid } from '@/assets/js/helper/guid'

import TransitionExpand from '@/components/private/TransitionExpand'
import HeaderMenuLevel from '@/components/public/layout/HeaderMenuLevel'

export default {
  name: 'HeaderMenu',
  components: {
    TransitionExpand,
    HeaderMenuLevel
  },
  props: {
    menu: {
      type: Object,
      require: true
    }
  },
  data() {
    return {
      state: false,
      levels: this.mapLevels(this.menu)
    }
  },
  computed: {
    flatLevels() {
      return getLevels(this.levels)

      function getLevels(level = {}, levels = []) {
        levels.push(level)

        if (level.items && level.items.length) {
          level.items.forEach((i) => {
            if (i.level) getLevels(i.level, levels)
          })
        }

        return levels
      }
    },
    activeLevels() {
      return this.flatLevels.filter((l) => l.active)
    }
  },
  methods: {
    mapLevels(level = {}, options = {}) {
      if (level.items && level.items.length) {
        options = Object.assign(
          {
            depth: 1,
            title: undefined,
            parentItem: null
          },
          options
        )

        if (options.depth <= COMPONENT_HEADERMENU_MAX_DEPTH_LEVEL) {
          return {
            id: guid(),
            depth: options.depth,
            active: false,
            title: options.title,
            parentTitle: options.parentTitle,
            parentItem: options.parentItem,
            items: mapItems.bind(this)(level.items),
            groupedItems: mapGroupedItems.bind(this)(level.groupedItems)
          }
        }

        return undefined
      }

      return undefined

      function mapItems(items = []) {
        return items.map((i) => {
          i.id = guid()
          i.level = this.mapLevels(
            {
              items: i.items,
              groupedItems: i.groupedItems
            },
            {
              depth: options.depth + 1,
              title: i.text,
              parentTitle: options.title || this.$t('headermenu.level0'),
              parentItem: {
                active: i.active,
                href: i.href,
                text: this.$t('headermenu.navigateto'),
                img: i.img
              }
            }
          )
          i.items = undefined
          i.img = undefined
          i.hasSublevel = i.level !== undefined

          return i
        })
      }

      function mapGroupedItems(groupedItems = null) {
        if (options.depth === COMPONENT_HEADERMENU_MAX_DEPTH_LEVEL && groupedItems) {
          return groupedItems.map((g) => {
            g.id = guid()
            g.items = mapItems.bind(this)(g.items)

            return g
          })
        }

        return undefined
      }
    },
    setActiveLevel(levelId = null) {
      if (levelId === null) {
        this.resetActiveLevel()
      } else {
        const activeLevel = this.flatLevels.find((l) => l.id === levelId) || {}
        const activeLevelDepth = activeLevel.depth || 1
        const deeperLevels = this.flatLevels.filter((l) => l.depth > activeLevelDepth)

        deeperLevels.forEach((l) => {
          l.active = false
        })
        activeLevel.active = true
      }
    },
    resetActiveLevel() {
      this.flatLevels.forEach((l) => {
        l.active = l.depth === 1 || l.items.some((i) => i.active) || (l.parentItem && l.parentItem.active)
      })
    },
    navigateUp({ event }) {
      const levelId = (this.activeLevels[this.activeLevels.length - 2] || {}).id || null
      this.setActiveLevel(levelId)
    },
    navigateDown({ event, hasSublevel, levelId }) {
      if (!this.$root.isDesktop && hasSublevel) {
        event.preventDefault()
        this.setActiveLevel(levelId)
      }
    }
  },
  created() {
    this.translationsAllowLateInitialization = false
    this.resetActiveLevel()
    this.$root.$on('headermenutoggle:toggle', (state) => {
      this.state = state
    })
  },
  watch: {
    state(state) {
      if (!state) this.resetActiveLevel()
      this.$root.$emit('scrollbar:toggle', { key: this.$options.name, state: this.state, from: 0, till: this.$root.md.value })
    }
  }
}
</script>

<style lang="scss">
// mobile
$headermenu-transition-speed-in: 250ms !default;
$headermenu-transition-speed-out: 200ms !default;

$headermenu-open-transition-in: opacity linear $headermenu-transition-speed-in, transform linear 0ms !default;
$headermenu-open-transition-out: opacity linear $headermenu-transition-speed-out, transform linear 0ms $headermenu-transition-speed-out !default;
$headermenu-open-level-transition-in: transform cubic-bezier(0.5, 0, 0.5, 0.5) $headermenu-transition-speed-in !default;
$headermenu-open-level-transition-out: transform cubic-bezier(0.5, 0, 0.5, 0.5) $headermenu-transition-speed-out !default;

$headermenu-level-transition-in: transform cubic-bezier(0.5, 0, 0.5, 0.5) 250ms !default;
$headermenu-level-transition-out: transform cubic-bezier(0.5, 0, 0.5, 0.5) 200ms !default;

// desktop
$headermenu-breakpoint: $desktop-breakpoint !default;

$headermenu-desktop-level1-gap: $spacer * 0.5 !default;

$headermenu-desktop-transition-in: height $header-collapse-transition-ease-in $header-collapse-transition-speed-in !default;
$headermenu-desktop-transition-out: height $header-collapse-transition-ease-out $header-collapse-transition-speed-out !default;

.header-menu {
  @include media-breakpoint-down(get-breakpoint-key($headermenu-breakpoint, -1)) {
    .menu-levels.mobile {
      position: fixed;
      inset: 0;
      overflow: hidden;
      z-index: $zindex-fixed;

      .menu-level {
        position: absolute;
        inset: 0;
        overflow: hidden;
        overflow-y: auto;

        &:last-child {
          position: relative;
          z-index: 2;
          height: 100%;
        }

        &.menulevel-enter,
        &.menulevel-leave-to {
          transform: translate3d(100%, 0, 0);
        }

        &.menulevel-enter-to,
        &.menulevel-leave {
          transform: translate3d(0%, 0, 0);
        }

        &.menulevel-enter-active {
          transition: $headermenu-level-transition-in;
        }

        &.menulevel-leave-active {
          transition: $headermenu-level-transition-out;
        }
      }

      &.menu-enter,
      &.menu-leave-to {
        opacity: 0.99;

        .menu-level {
          transform: translate3d(0, -100%, 0);
        }
      }

      &.menu-enter-to,
      &.menu-leave {
        opacity: 1;

        .menu-level {
          transform: translate3d(0, 0%, 0);
        }
      }

      &.menu-enter-active {
        transition: $headermenu-open-transition-in;

        .menu-level {
          transition: $headermenu-open-level-transition-in;
        }
      }

      &.menu-leave-active {
        transition: $headermenu-open-transition-out;

        .menu-level {
          transition: $headermenu-open-level-transition-out;
        }
      }
    }
  }

  @include media-breakpoint-up($headermenu-breakpoint) {
    .menu-levels.desktop {
      .menu-level {
        &.level-1 {
          margin-bottom: $headermenu-desktop-level1-gap;

          .menu-item {
            > .level-2 {
              display: none;
            }

            &.hover {
              > .level-2 {
                display: block;
              }
            }
          }
        }

        &.level-2 {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          margin-top: $headermenu-desktop-level1-gap * -1;
        }
      }

      &.menu-enter-active {
        overflow: hidden;
        transition: $headermenu-desktop-transition-in;
      }

      &.menu-leave-active {
        overflow: hidden;
        transition: $headermenu-desktop-transition-out;
      }
    }
  }
}
</style>
